import type { ReactNode } from 'react'
import { ApiRequestError } from '../api/client'
import { delta, deltaTone, number } from '../lib/format'
import './ui.css'

/**
 * Panel primitives from the handoff's §4 component inventory: stat tiles,
 * section headers, empty and error states, loading skeletons.
 */

/** The KPI tile used on screens 7a and 8h: numeral, CAPS label, delta. */
export function StatTile({
  label,
  value,
  changePct,
  hint,
  tone = 'default',
}: {
  label: string
  value: number | string
  changePct?: number | null
  hint?: string
  tone?: 'default' | 'urgent' | 'positive'
}) {
  const change = delta(changePct)

  return (
    <div className={`stat stat--${tone}`}>
      <div className="overline">{label}</div>
      <div className="stat__value numeral">
        {typeof value === 'number' ? number(value) : value}
      </div>
      {change && (
        <div className={`stat__delta stat__delta--${deltaTone(changePct)}`}>
          <span className="numeral">{change}</span>
          <span className="stat__delta-hint">{hint ?? 'keçən dövrə'}</span>
        </div>
      )}
      {!change && hint && <div className="stat__hint">{hint}</div>}
    </div>
  )
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string
  subtitle?: ReactNode
  actions?: ReactNode
}) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle && <p className="page-header__subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  )
}

export function Section({
  title,
  actions,
  children,
}: {
  title: string
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="card stack">
      <div className="row row--between">
        <h3>{title}</h3>
        {actions}
      </div>
      {children}
    </section>
  )
}

/**
 * Empty state (§4). The design pairs a Mimo illustration with a heading, an
 * explanation and up to two calls to action; the panel keeps the structure and
 * omits the mascot, which belongs to the app.
 */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {description && <p className="muted">{description}</p>}
      {action}
    </div>
  )
}

/** Renders the server's Azerbaijani copy rather than inventing its own. */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message =
    error instanceof ApiRequestError
      ? error.message
      : error instanceof Error
        ? error.message
        : 'Gözlənilməz xəta baş verdi.'

  const code = error instanceof ApiRequestError ? error.code : null

  return (
    <div className="error-state" role="alert">
      <h3>Bir şey səhv getdi</h3>
      <p>{message}</p>
      {code && <p className="error-state__code">{code}</p>}
      {onRetry && (
        <button type="button" className="btn btn--ghost btn--sm" onClick={onRetry}>
          Yenidən yoxla
        </button>
      )}
    </div>
  )
}

/** §5 shimmer, sized to the rows it stands in for. */
export function TableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="table" aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton skeleton--row" />
      ))}
    </div>
  )
}

export function CardSkeleton({ height = 180 }: { height?: number }) {
  return <div className="skeleton" style={{ height }} aria-busy="true" />
}

/** The horizontal bar list used for "Şikayət növləri" on screen 7a. */
export function BarList({
  items,
}: {
  items: { label: string; value: number; tone?: 'urgent' | 'default' }[]
}) {
  const max = Math.max(1, ...items.map((i) => i.value))

  return (
    <ul className="bars">
      {items.map((item) => (
        <li key={item.label} className="bars__item">
          <span className="bars__label">{item.label}</span>
          <span className="bars__track">
            <span
              className={`bars__fill bars__fill--${item.tone ?? 'default'}`}
              style={{ width: `${(item.value / max) * 100}%` }}
            />
          </span>
          <span className="bars__value numeral">{number(item.value)}</span>
        </li>
      ))}
    </ul>
  )
}

/** Avatar initials chip, matching the app's inverse-surface treatment. */
export function Avatar({ initials, title }: { initials: string; title?: string }) {
  return (
    <span className="avatar" title={title} aria-hidden={title ? undefined : true}>
      {initials}
    </span>
  )
}
